import * as React from "react";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { getPeriodRange, type PeriodKey, type PeriodRange } from "@/lib/period";

interface StoredPeriod {
  key: PeriodKey;
  customFrom?: string;
  customTo?: string;
}

interface PeriodContextValue {
  period: PeriodRange;
  setPeriodKey: (key: Exclude<PeriodKey, "custom">) => void;
  setCustomRange: (from: string, to: string) => void;
}

const PeriodContext = React.createContext<PeriodContextValue | undefined>(undefined);

export function PeriodProvider({ children }: { children: React.ReactNode }) {
  const [stored, setStored] = useLocalStorage<StoredPeriod>("eos-period", { key: "this_month" });

  const period = React.useMemo(
    () =>
      getPeriodRange(
        stored.key,
        stored.key === "custom" ? { from: stored.customFrom ?? "", to: stored.customTo ?? "" } : undefined,
      ),
    [stored],
  );

  const setPeriodKey = React.useCallback(
    (key: Exclude<PeriodKey, "custom">) => setStored({ key }),
    [setStored],
  );

  const setCustomRange = React.useCallback(
    (from: string, to: string) => setStored({ key: "custom", customFrom: from, customTo: to }),
    [setStored],
  );

  return <PeriodContext.Provider value={{ period, setPeriodKey, setCustomRange }}>{children}</PeriodContext.Provider>;
}

export function usePeriod() {
  const ctx = React.useContext(PeriodContext);
  if (!ctx) throw new Error("usePeriod must be used within PeriodProvider");
  return ctx;
}
